import { useState, useEffect } from 'react';

/**
 * useMediaQuery
 *
 * Returns whether the given CSS media query currently matches, and updates only
 * when the match state *flips* — i.e. when a breakpoint is crossed. Unlike a
 * resize listener that fires on every pixel, `matchMedia` notifies the listener
 * once per threshold crossing, so resizing within a breakpoint band triggers no
 * re-render at all.
 *
 * @param {string} query - A CSS media query, e.g. '(max-width: 1023.98px)'.
 * @returns {boolean} `true` while the query matches.
 *
 * @example
 *   const isCompact = useMediaQuery('(max-width: 1023.98px)');
 */
const getMatches = (query) =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches
    : false;

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => getMatches(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mql = window.matchMedia(query);
    // Sync in case the viewport changed between initial render and effect run.
    setMatches(mql.matches);

    const handler = (event) => setMatches(event.matches);
    // addEventListener is the modern API; addListener is the Safari < 14 fallback.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [query]);

  return matches;
};

export default useMediaQuery;
